import { useEffect, useMemo, useState } from "react";
import type { AuthContextValue } from "@/contexts/AuthContext";
import { LOCATIONS } from "@/config/locations";
import { requireSupabase } from "@/lib/supabase";
import { phoneDigits } from "./settingsDrawerPhone";

export function useSettingsDrawerAccount(auth: AuthContextValue | null) {
  const [accountOpen, setAccountOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [personalPhone, setPersonalPhone] = useState("");
  const [workPhone, setWorkPhone] = useState("");
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountMsg, setAccountMsg] = useState("");

  const suggestedOfficePhone = useMemo(() => {
    if (!auth || auth.role !== "sales") return "";
    const preferredLocationId = auth.activeLocationId || auth.accessibleLocationIds[0] || "";
    if (!preferredLocationId) return "";
    return phoneDigits(LOCATIONS.find((loc) => loc.id === preferredLocationId)?.phone?.trim() ?? "");
  }, [auth]);

  useEffect(() => {
    if (!auth?.profile) return;
    setFullName(auth.profile.full_name ?? "");
    setEmail(auth.profile.email ?? auth.session?.user.email ?? "");
    setPersonalPhone(phoneDigits(auth.profile.personal_phone ?? ""));
    setWorkPhone(
      phoneDigits(auth.profile.work_phone ?? (auth.role === "sales" && suggestedOfficePhone ? suggestedOfficePhone : ""))
    );
  }, [auth?.profile, auth?.role, auth?.session?.user.email, suggestedOfficePhone]);

  const normalizedEmail = email.trim().toLowerCase();
  const effectiveWorkPhone = phoneDigits(workPhone) || (auth?.role === "sales" && suggestedOfficePhone ? suggestedOfficePhone : "");
  const accountEssentialReady = !!fullName.trim() && !!normalizedEmail && !!effectiveWorkPhone;
  const incompleteAccountFields = [
    !fullName.trim() ? "full name" : null,
    !normalizedEmail ? "email" : null,
    !effectiveWorkPhone ? "work number" : null,
  ].filter(Boolean) as string[];

  const hasAccountChanges = !!auth?.profile && (
    fullName.trim() !== (auth.profile.full_name ?? "").trim() ||
    normalizedEmail !== (auth.profile.email ?? "").trim().toLowerCase() ||
    phoneDigits(personalPhone) !== phoneDigits(auth.profile.personal_phone ?? "") ||
    effectiveWorkPhone !== phoneDigits(auth.profile.work_phone ?? "")
  );

  useEffect(() => {
    if (!auth?.session || !auth.profile) return;
    if (!accountEssentialReady) setAccountOpen(true);
  }, [auth?.session, auth?.profile, accountEssentialReady]);

  async function saveAccountProfile() {
    if (!auth?.session || !auth.profile) return;

    const nextFullName = fullName.trim();
    const nextEmail = normalizedEmail;
    const nextPersonalPhone = phoneDigits(personalPhone);
    const nextWorkPhone = effectiveWorkPhone;

    if (!nextFullName || !nextEmail || !nextWorkPhone) {
      setAccountMsg(`Complete the required fields first: ${incompleteAccountFields.join(", ")}.`);
      return;
    }

    setAccountSaving(true);
    setAccountMsg("");

    try {
      const sb = requireSupabase();
      const currentEmail = (auth.profile.email ?? "").trim().toLowerCase();
      const emailChanged = nextEmail !== currentEmail;

      if (emailChanged || nextFullName !== (auth.profile.full_name ?? "").trim()) {
        const { error: authUpdateError } = await sb.auth.updateUser({
          ...(emailChanged ? { email: nextEmail } : {}),
          data: {
            full_name: nextFullName,
            display_name: auth.profile.display_name ?? nextFullName,
            name: nextFullName,
          },
        });

        if (authUpdateError) throw authUpdateError;
      }

      const nextDisplayName = auth.profile.display_name?.trim() || nextFullName;
      const { error: profileError } = await sb
        .from("profiles")
        .update({
          full_name: nextFullName,
          display_name: nextDisplayName,
          email: nextEmail,
          personal_phone: nextPersonalPhone,
          work_phone: nextWorkPhone || null,
        })
        .eq("id", auth.session.user.id);

      if (profileError) throw profileError;

      auth.refreshProfile();
      setAccountMsg(emailChanged ? "Profile saved. Check your inbox to confirm the email change." : "Profile saved.");
      if (nextFullName && nextEmail && nextWorkPhone) setAccountOpen(false);
    } catch (error) {
      setAccountMsg(error instanceof Error ? error.message : String(error));
    } finally {
      setAccountSaving(false);
    }
  }

  return {
    accountOpen,
    setAccountOpen,
    fullName,
    setFullName,
    email,
    setEmail,
    personalPhone,
    setPersonalPhone,
    workPhone,
    setWorkPhone,
    accountSaving,
    accountMsg,
    suggestedOfficePhone,
    normalizedEmail,
    effectiveWorkPhone,
    accountEssentialReady,
    incompleteAccountFields,
    hasAccountChanges,
    saveAccountProfile,
  };
}

export type SettingsDrawerAccountModel = ReturnType<typeof useSettingsDrawerAccount>;
